<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Alert, AlertV2, Text } from '@Shelly/shelly-design-system';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import type { Card } from 'src/components/SelectList/SelectList.types';
	import type { Device } from 'src/types/Device';
	import { devices_status } from 'src/stores/device-runtime/status';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		gatewayMonitorsOtherBluDevices,
		getAssociatedBleGatewaysForBluDevices,
	} from 'src/helpers/ble_devices_helpers';

	export let bluDevices: Device[] = [];
	export let transferDeviceIds: string[] = [];
	export let selectedGatewayIds: string[] = [];
	export let variant: 'legacy' | 'v2' = 'legacy';

	const partialWarningDefault = 'Moving this gateway may affect other BLU devices in this home.';

	let allItemsRefs: { [key: string | number]: HTMLElement } = {};

	$: transferringBluDeviceIds = bluDevices.map((device) => device.id);
	$: associatedGateways = getAssociatedBleGatewaysForBluDevices(bluDevices, transferDeviceIds);
	$: hasPartialWarningGateways = associatedGateways.some((gateway) =>
		gatewayMonitorsOtherBluDevices(gateway.id, transferringBluDeviceIds),
	);
	$: selectedCardsId = selectedGatewayIds.map((id) => ({ id }));

	$: gatewayCards = associatedGateways.map((gateway): Card => {
		const isOffline = !$devices_status[real_id(gateway.id)]?.online || !gateway.cloud_online;
		const monitorsOthers = gatewayMonitorsOtherBluDevices(gateway.id, transferringBluDeviceIds);
		const offlineText = $format('device_status_no_connection', {
			default: 'Device is offline',
		});
		const partialText = $format('alert_blu_transfer_gateway_partial_warning', {
			default: partialWarningDefault,
		});

		let descriptiveText: string | undefined;
		if (isOffline && monitorsOthers) {
			descriptiveText = `${offlineText}. ${partialText}`;
		} else if (isOffline) {
			descriptiveText = offlineText;
		} else if (monitorsOthers) {
			descriptiveText = partialText;
		}

		return {
			id: gateway.id,
			name: gateway.name,
			image: gateway.image,
			backgroundColor: gateway.backgroundColor,
			icon: gateway.icon,
			disabled: isOffline,
			descriptiveText,
			selected: selectedGatewayIds.includes(gateway.id),
		};
	});

	const handleSelection = (card: Card): void => {
		if (card.disabled || card.id == null) return;

		const gatewayId = String(card.id);
		if (selectedGatewayIds.includes(gatewayId)) {
			selectedGatewayIds = selectedGatewayIds.filter((id) => id !== gatewayId);
		} else {
			selectedGatewayIds = [...selectedGatewayIds, gatewayId];
		}
	};
</script>

{#if variant === 'v2'}
	<AlertV2
		class="mb-m"
		heading={$format('alert_blu_transfer_gateway_warning_heading', {
			default: 'BLU device will lose connectivity',
		})}
		message={$format('alert_blu_transfer_gateway_warning_body', {
			default:
				'Listeners, repeaters, and gateways stay in the current account or home. Without them, the device may appear <span class=\"text-bold\">offline</span> after transfer. <span class=\"text-bold\">Add a new listener</span> in the destination or <span class=\"text-bold\">move existing ones</span>.',
		})}
	/>
{:else}
	<Alert
		class="mb-s"
		type="warning"
		heading={$format('alert_blu_transfer_gateway_warning_heading', {
			default: 'BLU device will lose connectivity',
		})}
		message={$format('alert_blu_transfer_gateway_warning_body', {
			default:
				'Listeners, repeaters, and gateways stay in the current account or home. Without them, the device may appear <span class=\"text-bold\">offline</span> after transfer. <span class=\"text-bold\">Add a new listener</span> in the destination or <span class=\"text-bold\">move existing ones</span>.',
		})}
	/>
{/if}

{#if hasPartialWarningGateways}
	{#if variant === 'v2'}
		<AlertV2
			class="mb-m"
			heading={$format('alert_blu_transfer_gateway_partial_warning', {
				default: partialWarningDefault,
			})}
		/>
	{:else}
		<Alert
			class="mb-s"
			type="warning"
			heading={$format('alert_blu_transfer_gateway_partial_warning', {
				default: partialWarningDefault,
			})}
		/>
	{/if}
{/if}

{#if gatewayCards.length > 0}
	<Text element="h3" styleType="body-small" class="mb-s">
		{$format('text_blu_transfer_gateway_list_title', {
			default: 'Associated listeners / gateways',
		})}
	</Text>
	<SelectList
		withSearch={false}
		showTitle={false}
		showTitleSearchSection={false}
		variant="checkbox"
		list={gatewayCards}
		{selectedCardsId}
		bind:allCardsRefs={allItemsRefs}
		on:click={(event) => handleSelection(event.detail)}
	/>
{/if}
